import { useRef, useCallback } from 'react';
import React from 'react';

// Shared touch gesture recognizer for pan/pinch-zoom surfaces (GH-54: battle grid + hex world
// map were mouse-only). One-finger drag pans, two-finger pinch zooms, and a tap (movement below
// TAP_MOVE_THRESHOLD_PX and duration below TAP_MAX_DURATION_MS) is left alone so the browser's
// normal touch->click synthesis still fires the underlying tile's onClick - we only start calling
// preventDefault once a gesture is unambiguously a pan/pinch, so we don't have to reimplement tap
// dispatch ourselves.
const TAP_MOVE_THRESHOLD_PX = 10;
const TAP_MAX_DURATION_MS = 300;

interface UseTouchPanZoomOptions {
  panOffset: { x: number; y: number };
  setPanOffset: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  zoom: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  minZoom: number;
  maxZoom: number;
}

type GestureMode = 'none' | 'pan' | 'pinch';

interface TouchGestureState {
  mode: GestureMode;
  startX: number;
  startY: number;
  startTime: number;
  hasMoved: boolean;
  panBaseX: number;
  panBaseY: number;
  pinchStartDist: number;
  pinchStartZoom: number;
}

const distanceBetween = (a: React.Touch, b: React.Touch) =>
  Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);

export function useTouchPanZoom({ panOffset, setPanOffset, zoom, setZoom, minZoom, maxZoom }: UseTouchPanZoomOptions) {
  const stateRef = useRef<TouchGestureState>({
    mode: 'none', startX: 0, startY: 0, startTime: 0, hasMoved: false,
    panBaseX: 0, panBaseY: 0, pinchStartDist: 0, pinchStartZoom: 1,
  });

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touches = e.touches;
    const state = stateRef.current;
    if (touches.length === 1) {
      const t = touches[0];
      state.mode = 'pan';
      state.startX = t.clientX;
      state.startY = t.clientY;
      state.startTime = Date.now();
      state.hasMoved = false;
      state.panBaseX = t.clientX - panOffset.x;
      state.panBaseY = t.clientY - panOffset.y;
    } else if (touches.length === 2) {
      state.mode = 'pinch';
      state.pinchStartDist = distanceBetween(touches[0], touches[1]);
      state.pinchStartZoom = zoom;
      state.hasMoved = true; // multi-touch is never a tap
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panOffset.x, panOffset.y, zoom]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const state = stateRef.current;
    const touches = e.touches;

    if (touches.length === 2) {
      if (state.mode !== 'pinch') {
        state.mode = 'pinch';
        state.pinchStartDist = distanceBetween(touches[0], touches[1]);
        state.pinchStartZoom = zoom;
        state.hasMoved = true;
        return;
      }
      e.preventDefault();
      if (state.pinchStartDist > 0) {
        const dist = distanceBetween(touches[0], touches[1]);
        const newZoom = Math.min(maxZoom, Math.max(minZoom, state.pinchStartZoom * (dist / state.pinchStartDist)));
        setZoom(newZoom);
      }
      return;
    }

    if (state.mode === 'pan' && touches.length === 1) {
      const t = touches[0];
      const dx = t.clientX - state.startX;
      const dy = t.clientY - state.startY;
      if (!state.hasMoved && Math.hypot(dx, dy) > TAP_MOVE_THRESHOLD_PX) {
        state.hasMoved = true;
      }
      if (state.hasMoved) {
        e.preventDefault();
        setPanOffset({ x: t.clientX - state.panBaseX, y: t.clientY - state.panBaseY });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom, minZoom, maxZoom, setZoom, setPanOffset]);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const state = stateRef.current;
    const remaining = e.touches.length;

    if (remaining === 0) {
      const wasTap = !state.hasMoved && Date.now() - state.startTime < TAP_MAX_DURATION_MS;
      if (!wasTap) {
        // Suppress the ghost click the browser would otherwise synthesize after a pan/pinch.
        e.preventDefault();
      }
      state.mode = 'none';
    } else if (remaining === 1) {
      // Released one finger out of a pinch - resume single-finger pan from here rather than
      // snapping (recompute the pan baseline against the current offset).
      const t = e.touches[0];
      state.mode = 'pan';
      state.startX = t.clientX;
      state.startY = t.clientY;
      state.startTime = Date.now();
      state.hasMoved = true;
      state.panBaseX = t.clientX - panOffset.x;
      state.panBaseY = t.clientY - panOffset.y;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panOffset.x, panOffset.y]);

  return { onTouchStart, onTouchMove, onTouchEnd };
}
